import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchConfigOptions, type ConfigOptionListParams } from '../services/smr-admin';

type ConfigOptionGroup = NonNullable<ConfigOptionListParams['group']>;

export const configOptionComboboxKeys = {
    all: ['smr-admin', 'config-options', 'combobox'] as const,
    // `group` is included so a group-scoped combobox (e.g. the campaign
    // Cancellation Rules add form, scoped to `policies`) caches independently of
    // the default unfiltered picker. `undefined` is dropped by React Query's key
    // hashing, so existing unfiltered callers keep their original cache key.
    page: (search: string, page: number, perPage: number, group?: ConfigOptionGroup) =>
        [...configOptionComboboxKeys.all, { search, page, perPage, group }] as const,
};

export function useConfigOptionsComboboxQuery(params: {
    search: string;
    page: number;
    perPage?: number;
    /** When set, scopes the catalogue query to a single group. Omitted = all groups. */
    group?: ConfigOptionGroup;
}) {
    const perPage = params.perPage ?? 20;
    return useQuery({
        queryKey: configOptionComboboxKeys.page(params.search, params.page, perPage, params.group),
        queryFn: () =>
            fetchConfigOptions({
                // Explicit empty-string check so a search of "0" is still forwarded
                // to the backend (truthy-coerce dropped "0" previously).
                search: params.search === '' ? undefined : params.search,
                page: params.page,
                per_page: perPage,
                group: params.group,
            }),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });
}
